import React, { useImperativeHandle, useRef, useState } from "react";
import {
  Body1,
  Button,
  Caption1,
  Field,
  Input,
  Subtitle2,
  Text,
} from "@fluentui/react-components";
import { AddRegular } from "@fluentui/react-icons/svg/add";
import { ArrowRightRegular } from "@fluentui/react-icons/svg/arrow-right";
import { CheckmarkCircleFilled } from "@fluentui/react-icons/svg/checkmark-circle";
import { DismissRegular } from "@fluentui/react-icons/svg/dismiss";
import { ReOrderDotsVerticalRegular } from "@fluentui/react-icons/svg/re-order-dots-vertical";
import { useTranslation } from "react-i18next";
import type { BoardTemplate } from "@onetasklist/shared";
import { useWorkflowSetupStepViewModel } from "./WorkflowSetupStep.ViewModel";
import { useWorkflowSetupStepText } from "./WorkflowSetupStep.i18n";
import type { WizardColumn } from "./WorkflowSetupStep.ViewModel.types";
import "../onboarding-wizard.scss";

export interface WorkflowSetupStepHandle {
  finish: () => Promise<void>;
}

interface WorkflowSetupStepProps {
  wizardBoardId: string;
  onFinish: () => Promise<void>;
  onIsSavingChange?: (v: boolean) => void;
}

const TemplateCard: React.FC<{
  template: BoardTemplate;
  selected: boolean;
  onSelect: (template: BoardTemplate) => void;
  selectText: string;
  selectedText: string;
}> = ({ template, selected, onSelect, selectText, selectedText }) => (
  <div
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    className={`workflow-setup-step__template${selected ? " workflow-setup-step__template--selected" : ""}`}
    onClick={() => onSelect(template)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(template);
      }
    }}
  >
    {selected && <CheckmarkCircleFilled className="workflow-setup-step__check-badge" aria-hidden />}
    <Text
      className={`workflow-setup-step__template-title${selected ? " workflow-setup-step__template-title--selected" : ""}`}
    >
      {template.title}
    </Text>
    <Caption1 className="workflow-setup-step__template-desc">{template.description}</Caption1>
    <div className="workflow-setup-step__column-list">
      {template.statuses.map((s, i) => (
        <div key={i} className="workflow-setup-step__column-chip">
          {s.name}
        </div>
      ))}
    </div>
    <Button
      className="workflow-setup-step__select-btn"
      size="small"
      appearance={selected ? "primary" : "secondary"}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(template);
      }}
    >
      {selected ? selectedText : selectText}
    </Button>
  </div>
);

const ColumnChip: React.FC<{
  col: WizardColumn;
  index: number;
  totalColumns: number;
  isDragging: boolean;
  dragIndexRef: React.MutableRefObject<number | null>;
  setDraggingId: (id: string | null) => void;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  onDrop: (targetIndex: number) => void;
  onSetDone: (id: string) => void;
  doneLabel: string;
}> = ({
  col,
  index,
  totalColumns,
  isDragging,
  dragIndexRef,
  setDraggingId,
  onRename,
  onRemove,
  onDrop,
  onSetDone,
  doneLabel,
}) => (
  <div className="workflow-setup-step__column-wrap">
    <div
      className={[
        "workflow-setup-step__column",
        isDragging ? "workflow-setup-step__column--dragging" : "",
        col.isDone ? "workflow-setup-step__column--done" : "",
      ].filter(Boolean).join(" ")}
      style={{ backgroundColor: col.color }}
      draggable
      onDragStart={() => {
        dragIndexRef.current = index;
        setDraggingId(col.id);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => onDrop(index)}
      onDragEnd={() => {
        dragIndexRef.current = null;
        setDraggingId(null);
      }}
    >
      <div className="workflow-setup-step__column-head">
        <ReOrderDotsVerticalRegular className="workflow-setup-step__grip" aria-hidden />
        <input
          className="workflow-setup-step__column-name-input"
          value={col.name}
          aria-label={`Column name: ${col.name}`}
          onChange={(e) => onRename(col.id, e.target.value)}
        />
        <Button
          className="workflow-setup-step__remove-btn"
          appearance="subtle"
          size="small"
          icon={<DismissRegular />}
          aria-label={`Remove ${col.name} column`}
          onClick={() => onRemove(col.id)}
        />
      </div>
      <div className="workflow-setup-step__column-footer">
        <button
          type="button"
          role="radio"
          aria-checked={col.isDone}
          className={`workflow-setup-step__done-toggle${!col.isDone ? " workflow-setup-step__done-toggle--inactive" : ""}`}
          onClick={() => onSetDone(col.id)}
          aria-label={`Mark ${col.name} as done column`}
        >
          {doneLabel}
        </button>
      </div>
    </div>
    {index < totalColumns - 1 && (
      <ArrowRightRegular className="workflow-setup-step__arrow" aria-hidden />
    )}
  </div>
);

export const WorkflowSetupStep = React.forwardRef<WorkflowSetupStepHandle, WorkflowSetupStepProps>(
  ({ wizardBoardId, onFinish, onIsSavingChange }, ref) => {
  const { t } = useTranslation();
  const text = useWorkflowSetupStepText(t);
  const vm = useWorkflowSetupStepViewModel({ wizardBoardId, onFinish, onIsSavingChange });

  useImperativeHandle(ref, () => ({ finish: vm.finish }), [vm.finish]);

  const dragIndexRef = useRef<number | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  return (
    <div className="workflow-setup-step">
      <div className="workflow-setup-step__header">
        <Caption1 className="workflow-setup-step__step-label">{text.stepLabelText}</Caption1>
        <Subtitle2>{text.titleText}</Subtitle2>
        <Body1>{text.subtitleText}</Body1>
      </div>

      <Field label={vm.boardNameLabelText}>
        <Input
          value={vm.boardName}
          maxLength={50}
          onChange={(_, data) => vm.setBoardName(data.value)}
          contentAfter={
            <Caption1>{`${vm.boardName.length} / 50`}</Caption1>
          }
        />
      </Field>

      <div className="workflow-setup-step__section">
        <Subtitle2>{vm.workflowSectionText}</Subtitle2>
        <Caption1 className="workflow-setup-step__hint">{vm.workflowHintText}</Caption1>

        <div className="workflow-setup-step__columns-row" role="list" aria-label={text.workflowColumnsAriaLabel}>
          {vm.columns.map((col, index) => (
            <ColumnChip
              key={col.id}
              col={col}
              index={index}
              totalColumns={vm.columns.length}
              isDragging={draggingId === col.id}
              dragIndexRef={dragIndexRef}
              setDraggingId={setDraggingId}
              onRename={vm.renameColumn}
              onRemove={vm.removeColumn}
              onDrop={(toIndex) => {
                if (dragIndexRef.current !== null) {
                  vm.moveColumn(dragIndexRef.current, toIndex);
                }
              }}
              onSetDone={vm.setDoneColumn}
              doneLabel={vm.doneColumnLabelText}
            />
          ))}
        </div>

        <Button
          className="workflow-setup-step__add-column-btn"
          appearance="subtle"
          icon={<AddRegular />}
          onClick={vm.addColumn}
        >
          {vm.addColumnText}
        </Button>
      </div>

      <div className="workflow-setup-step__section">
        <Subtitle2>{vm.templatesSectionText}</Subtitle2>
        <Caption1 className="workflow-setup-step__hint">{vm.templatesHintText}</Caption1>

        <div
          className="workflow-setup-step__templates-grid"
          role="radiogroup"
          aria-label={text.templatesAriaLabel}
        >
          {vm.templates.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              selected={tpl.id === vm.selectedTemplateId}
              onSelect={vm.selectTemplate}
              selectText={text.selectTemplateText}
              selectedText={text.selectedText}
            />
          ))}
        </div>
      </div>

    </div>
  );
});
